// Copyright (C) 2020-2022 Intel Corporation
//
// SPDX-License-Identifier: MIT

import React, {
    useState, useEffect, useRef, useCallback,
} from 'react';
import { Col } from 'antd/lib/grid';
import Select from 'antd/lib/select';
import Radio, { RadioChangeEvent } from 'antd/lib/radio';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox';
import InputNumber from 'antd/lib/input-number';
import Text from 'antd/lib/typography/Text';

import { CopyOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { copyAttributeValue } from 'actions/annotation-actions';

import config from 'config';
import { clamp } from 'utils/math';
import TextArea, { TextAreaRef } from 'antd/lib/input/TextArea';
import Image from 'antd/lib/image';
import Dropdown from 'antd/lib/dropdown';
import Tooltip from 'antd/lib/tooltip';
import Button from 'antd/lib/button';
import Badge from 'antd/lib/badge';
import Tag from 'antd/lib/tag';
import type { SceneAttribute } from 'components/annotation-page/workspace/scene/scene-model';
import { t } from 'utils/i18n';
import ColorPicker from './color-picker';

interface Props {
    readonly: boolean;
    attrInputType: string;
    attrValues: string[];
    selectValues: object[];
    attrValue: string;
    attrName: string;
    attrID: number;
    changeAttribute(attrID: number, value: string): void;
    // the task's Scene value of this attribute, when it has one
    scene?: SceneAttribute | null;
}

function attrIsTheSame(prevProps: Props, nextProps: Props): boolean {
    return (
        nextProps.selectValues === prevProps.selectValues &&
        nextProps.readonly === prevProps.readonly &&
        nextProps.attrID === prevProps.attrID &&
        nextProps.attrValue === prevProps.attrValue &&
        nextProps.attrName === prevProps.attrName &&
        nextProps.attrInputType === prevProps.attrInputType &&
        nextProps.scene?.value === prevProps.scene?.value &&
        nextProps.scene?.emptyName === prevProps.scene?.emptyName &&
        nextProps.scene?.options === prevProps.scene?.options &&
        nextProps.attrValues
            .map((value: string, id: number): boolean => prevProps.attrValues[id] === value)
            .every((value: boolean): boolean => value)
    );
}

function ItemAttributeComponent(props: Props): JSX.Element {
    const {
        attrInputType, attrValues, attrValue,
        attrName, attrID, readonly, selectValues, changeAttribute, scene,
    } = props;

    const attrNameStyle: React.CSSProperties = { wordBreak: 'break-word', lineHeight: '1em', fontSize: 12 };
    const ref = useRef<TextAreaRef>(null);
    const [selectionStart, setSelectionStart] = useState<number>(attrValue.length);
    const [localAttrValue, setAttributeValue] = useState(attrValue);
    const baseURL = window.location.origin;

    useEffect(() => {
        // attribute value updated from inside the app (for example undo/redo)
        if (attrValue !== localAttrValue) {
            setAttributeValue(attrValue);
        }
    }, [attrValue]);

    const dispatch = useDispatch();
    const isCopyDisabled = !localAttrValue?.trim();

    const handleCopy = useCallback(() => {
        dispatch(copyAttributeValue(attrName, localAttrValue));
    }, [dispatch, attrName, attrValue]);

    useEffect(() => {
        // wrap to internal use effect to avoid issues
        // with chinese keyboard
        // https://github.com/opencv/cvat/pull/6916
        if (localAttrValue !== attrValue) {
            changeAttribute(attrID, localAttrValue);
        }
    }, [localAttrValue]);

    useEffect(() => {
        const textArea = ref?.current?.resizableTextArea?.textArea;
        if (textArea instanceof HTMLTextAreaElement) {
            textArea.selectionStart = selectionStart;
            textArea.selectionEnd = selectionStart;
        }
    }, [attrValue]);

    if (attrInputType === 'checkbox') {
        return (
            <Col span={24}>
                <Checkbox
                    className='cvat-object-item-checkbox-attribute'
                    checked={localAttrValue === 'true'}
                    disabled={readonly}
                    onChange={(event: CheckboxChangeEvent): void => {
                        setAttributeValue(event.target.checked ? 'true' : 'false');
                    }}
                >
                    <Text style={attrNameStyle} className='cvat-text'>
                        {attrName}
                    </Text>
                </Checkbox>
            </Col>
        );
    }

    if (attrInputType === 'radio') {
        return (
            <Col span={24}>
                <fieldset className='cvat-object-item-radio-attribute'>
                    <legend>
                        <Text style={attrNameStyle} className='cvat-text'>
                            {attrName}
                        </Text>
                    </legend>
                    <Radio.Group
                        disabled={readonly}
                        size='small'
                        value={localAttrValue}
                        onChange={(event: RadioChangeEvent): void => {
                            setAttributeValue(event.target.value);
                        }}
                    >
                        {attrValues.map(
                            (value: string): JSX.Element => (
                                <Radio key={value} value={value}>
                                    {value === config.UNDEFINED_ATTRIBUTE_VALUE ? config.NO_BREAK_SPACE : value}
                                </Radio>
                            ),
                        )}
                    </Radio.Group>
                </fieldset>
            </Col>
        );
    }

    if (attrInputType === 'select') {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', width: '100%', overflow: 'hidden',
            }}
            >
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', flexShrink: 0,
                }}
                >
                    <Text className='cvat-text'>{attrName}</Text>
                    <CopyOutlined
                        className={`copy-icon ${isCopyDisabled ? 'disabled' : ''}`}
                        onClick={handleCopy}
                    />
                </div>
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Select
                        disabled={readonly}
                        size='small'
                        onChange={(value: string): void => {
                            setAttributeValue(value);
                        }}
                        value={localAttrValue}
                        className='cvat-object-item-select-attribute'
                        style={{ width: '100%' }}
                    >
                        {attrValues.map(
                            (value: string): JSX.Element => (
                                <Select.Option key={value} value={value}>
                                    {value === config.UNDEFINED_ATTRIBUTE_VALUE ? config.NO_BREAK_SPACE : value}
                                </Select.Option>
                            ),
                        )}
                    </Select>
                </div>
            </div>
        );
    }

    if (attrInputType === 'color' || (attrName === 'color' && attrInputType === 'text')) {
        const currentValue = !localAttrValue || localAttrValue === '#' ?
            (attrValues[0] || '#000000') :
            localAttrValue;

        const displayValue = !localAttrValue || localAttrValue === '#' ?
            '#000000' :
            currentValue.toUpperCase();

        return (
            <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', width: '100%', overflow: 'hidden',
            }}
            >
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', flex: 2,
                }}
                >
                    <Text className='cvat-text'>{attrName}</Text>
                    <CopyOutlined
                        className={`copy-icon ${isCopyDisabled ? 'disabled' : ''}`}
                        onClick={handleCopy}
                    />
                </div>
                <div style={{ display: 'flex', flex: 6 }}>
                    <ColorPicker
                        placement='bottom'
                        value={currentValue}
                        onChange={(color) => {
                            setAttributeValue(color);
                        }}
                    >
                        <Button type='default' style={{ display: 'flex', width: '100%' }}>
                            <Badge
                                className='cvat-change-task-label-color-badge'
                                color={currentValue}
                            />
                            <Text style={{ fontWeight: 500 }}>{displayValue}</Text>
                        </Button>
                    </ColorPicker>
                </div>
            </div>
        );
    }

    if (attrInputType === 'select_attr') {
        const parsedAttrValues = Array.isArray(attrValues) ?
            attrValues.map((str) => {
                try {
                    return str ? JSON.parse(str) : null;
                } catch (error) {
                    console.error('Invalid JSON:', str, error);
                    return null;
                }
            }).filter((obj) => obj && obj.name && obj.path) :
            [];

        const modelAttribute = ['spot_model', 'blend_model', 'tule_model', 'curtain_model'].includes(attrName);
        const values = modelAttribute && parsedAttrValues.length > 0 ? parsedAttrValues : selectValues;
        // a shape just drawn has no options of its own; an empty value is named, not blank
        let options = values?.length ? values : scene?.options ?? values;
        if (scene && !options.some((option: any) => option.path === '')) {
            options = [{ name: scene.emptyName, path: '', image_path: scene.emptyPreview }, ...options];
        }
        const own = !!scene && localAttrValue !== scene.value;

        return (
            <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', width: '100%', overflow: 'hidden',
            }}
            >
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', flexShrink: 0,
                }}
                >
                    <Text className='cvat-text'>{attrName}</Text>
                    <CopyOutlined
                        className={`copy-icon ${isCopyDisabled ? 'disabled' : ''}`}
                        onClick={handleCopy}
                    />
                </div>
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Select
                        disabled={readonly}
                        size='small'
                        onChange={(value: string): void => {
                            setAttributeValue(value);
                        }}
                        value={localAttrValue}
                        className='cvat-object-item-select-attribute'
                        style={{ width: '100%' }}
                    >
                        {options && options.map(
                            (value: any): JSX.Element => (
                                <Select.Option key={value.name} value={value.path}>
                                    <Dropdown
                                        destroyPopupOnHide
                                        overlayStyle={{ marginRight: '190px' }}
                                        overlay={(
                                            <Image
                                                className='cvat-interactor-tip-image'
                                                preview={false}
                                                src={`${baseURL}/api/library_preview/${value.image_path}`}
                                                fallback={require('../../../../assets/empty_image.jpg')}
                                            />
                                        )}
                                        placement='bottomRight'
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <Tooltip
                                                title={value.name}
                                                style={{
                                                    flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {value.name === config.UNDEFINED_ATTRIBUTE_VALUE ?
                                                    config.NO_BREAK_SPACE : value.name}
                                            </Tooltip>
                                        </div>
                                    </Dropdown>
                                </Select.Option>
                            ),
                        )}
                    </Select>
                    {own && (
                        <div className='cvat-object-item-scene-own'>
                            <Tag color='blue'>{t('Own')}</Tag>
                            <Button
                                type='link'
                                size='small'
                                disabled={readonly}
                                title={t('As in the scene: {name}', { name: scene.shortName })}
                                onClick={() => setAttributeValue(scene.value)}
                            >
                                {`↺ ${t('As in the scene: {name}', { name: scene.shortName })}`}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (attrInputType === 'number') {
        const [min, max, step] = attrValues.map((value: string): number => +value);
        return (
            <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', width: '100%', overflow: 'hidden',
            }}
            >
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', flexShrink: 0,
                }}
                >
                    <Text className='cvat-text'>{attrName}</Text>
                    <CopyOutlined
                        className={`copy-icon ${isCopyDisabled ? 'disabled' : ''}`}
                        onClick={handleCopy}
                    />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <InputNumber
                        disabled={readonly}
                        size='small'
                        onChange={(value: number | null): void => {
                            if (value !== null) {
                                setAttributeValue(`${clamp(+value, min, max)}`);
                            }
                        }}
                        value={+localAttrValue}
                        className='cvat-object-item-number-attribute'
                        min={min}
                        max={max}
                        step={step}
                        style={{ width: '100%' }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: '8px', width: '100%', overflow: 'hidden',
        }}
        >
            <div style={{
                display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', flexShrink: 0,
            }}
            >
                <Text className='cvat-text'>{attrName}</Text>
                <CopyOutlined
                    className={`copy-icon ${isCopyDisabled ? 'disabled' : ''}`}
                    onClick={handleCopy}
                />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <TextArea
                    ref={ref}
                    size='small'
                    disabled={readonly}
                    style={{
                        height: Math.min(120, attrValue.split('\n').length * 24),
                        minHeight: Math.min(120, attrValue.split('\n').length * 24),
                        width: '100%',
                        resize: 'vertical',
                    }}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void => {
                        if (ref.current?.resizableTextArea?.textArea) {
                            setSelectionStart(ref.current.resizableTextArea.textArea.selectionStart);
                        }
                        setAttributeValue(event.target.value);
                    }}
                    value={localAttrValue}
                    className='cvat-object-item-text-attribute'
                />
            </div>
        </div>
    );
}

export default React.memo(ItemAttributeComponent, attrIsTheSame);
